/**
 * Slash-command menu catalog — unified surface (Spec 29 follow-up).
 *
 * The grammar (``parseCommand`` in grammar.ts) is the authoritative
 * parser; this module is the discoverability surface. Every menu
 * cmd maps 1:1 to something parseCommand recognises.
 *
 * Post-palette-retirement (2026-04-23): this catalog is the ONLY
 * command surface. The separate ⇧⌘P Command Palette is retired;
 * its commands (mode toggle, workspace switch, library review,
 * stop-run, settings, logout) moved into this catalog. Typing "/"
 * opens the menu; filter-as-you-type narrows it.
 *
 * SRP: pure data + small pure predicates. No React, no rendering.
 * The CommandMenu component renders; tests pin the catalog shape,
 * the auto-open predicate, the state-aware visibility predicate,
 * and the filter-as-you-type matcher.
 *
 * # DECISION: descriptions are written as "what will happen when
 * #   the writer picks this", not mnemonics.
 * # WHY: writer feedback 2026-04-22 — terse descs read like
 * #      reminders for someone who already knew the command. The
 * #      rewrite rule is "read the desc, know the effect without
 * #      trying it."
 * # DATE: 2026-04-22
 *
 * # DECISION: unimplemented commands stay in the catalog with
 * #   ``disabled: true`` rather than being hidden.
 * # WHY: discoverability — writers learn the full vocabulary by
 * #      skimming the menu. Hiding /undo makes it invisible until
 * #      it ships. Dimmed row + "coming soon" tooltip tells the
 * #      truth without lying by omission.
 * # DATE: 2026-04-22
 *
 * # DECISION: state-aware groups hide entire sections that aren't
 * #   meaningful in the current state (e.g. "Filter" hidden in
 * #   idle), while individual items within a visible group can
 * #   be disabled-with-reason rather than hidden.
 * # WHY: two tiers of honesty. Hiding a whole group keeps the
 * #      idle menu short + focused on /draft. Disabling (not
 * #      hiding) individual items preserves vocabulary discovery.
 * # DATE: 2026-04-23
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type MenuGroupLabel = "Start" | "Pick" | "Navigate" | "Filter" | "Act" | "System";

export interface MenuItem {
  /** Exact slash-string the writer would type. Includes the
   *  leading ``/``. Click → prefill the input with this. */
  cmd: string;
  /** Plain-English description. Reads like prose; no mnemonics. */
  desc: string;
  /** Optional single-character keybind hint (renders as a small
   *  chip on the right of the row). */
  keybind?: string;
  /** True when the command is parseable but the handler isn't
   *  wired yet (currently /undo only). Menu dims + blocks click;
   *  manual type still surfaces a "coming soon" error. */
  disabled?: boolean;
  /** Optional "why this is disabled right now" hint, shown as the
   *  chip tooltip. Separate from ``disabled`` so we can also
   *  disable items contextually (e.g. /stop when idle — grammar
   *  is valid, behaviour isn't). */
  disabledReason?: string;
}

export interface MenuGroup {
  label: MenuGroupLabel;
  items: ReadonlyArray<MenuItem>;
}

/** Narrow state shape the menu filter reads. Kept local — the
 *  shell builds this from its own reducer / palette-context. */
export interface MenuRenderState {
  runState: "idle" | "mid-run" | "pausing" | "approval" | "paused" | "post-run";
  /** True when any EventLog entries exist (drives Filter /
   *  /replay / /export / /latest availability). */
  hasEvents: boolean;
  /** True when ``sections`` has at least one entry (drives /jump
   *  / /next availability). */
  hasSections: boolean;
  /** Workspaces available to the writer. One menu item per
   *  workspace renders in the Pick group. Empty / singleton =
   *  no per-workspace rows. */
  workspaces: ReadonlyArray<{ id: string; name: string; isCurrent: boolean }>;
  /** Current backend sandbox flag — drives /mode's label
   *  ("Switch to LIVE" vs "Switch to SANDBOX"). */
  isSandbox: boolean;
  /** True while a mode-flip request is in flight — disables /mode
   *  with a "toggling…" reason. */
  modeToggling: boolean;
  /** True when toggling mode is allowed — false mid-run (backend
   *  returns 409). Drives /mode's disabled state. */
  modeToggleAllowed: boolean;
  /**
   * Feature flag: ``advanced-commands``.
   * When false, the menu is restricted to ALWAYS_ALLOWED_COMMANDS.
   * When true (default for flagged users), the full menu is shown.
   */
  advancedCommandsEnabled: boolean;
}

// ---------------------------------------------------------------------------
// Feature-flag gate — commands always visible regardless of flag state
// ---------------------------------------------------------------------------

/**
 * Commands that are always shown, even when the ``advanced-commands``
 * feature flag is off. All other commands require the flag to be enabled.
 * ``/regen`` is core drafting-loop (the post-run counterpart of
 * ``/draft``), not an advanced workflow command.
 */
export const ALWAYS_ALLOWED_COMMANDS: ReadonlySet<string> = new Set([
  "/draft",
  "/library",
  "/regen",
  // Core post-run writer action — surfaced in the palette even when the
  // advanced-commands flag is off (same treatment as /regen).
  "/abbreviations",
]);

// ---------------------------------------------------------------------------
// Base catalog — every command we ship, regardless of state
// ---------------------------------------------------------------------------

const START_GROUP: MenuGroup = {
  label: "Start",
  items: [
    {
      cmd: "/draft",
      desc: "start a new draft (pick doctype, library, sections)",
      keybind: "D",
    },
  ],
};

const PICK_BASE: ReadonlyArray<MenuItem> = [
  {
    cmd: "/library",
    desc: "open the library hub — review, upload, delete, approve",
  },
  {
    cmd: "/upload",
    desc: "open the library hub — review, upload, delete, approve",
    keybind: "U",
  },
  {
    cmd: "/workspace",
    desc: "switch to a different workspace (add 'workspace <name>' to go directly)",
  },
  // /mode is state-dependent — its label + disabled state come
  // from buildMenuGroups, not the base catalog.
];

const NAVIGATE_ITEMS: ReadonlyArray<MenuItem> = [
  { cmd: "/spine", desc: "show the structural section list", keybind: "S" },
  { cmd: "/timeline", desc: "show everything that happened, newest first" },
  {
    cmd: "/jump §",
    desc: "jump to a section (pane AND Word doc) — e.g. /jump §9.2",
    keybind: "J",
  },
  { cmd: "/next", desc: "jump to the next section that needs you" },
  {
    cmd: "/latest",
    desc: "scroll the timeline to the most recent event",
    keybind: "↓",
  },
];

const FILTER_ITEMS: ReadonlyArray<MenuItem> = [
  { cmd: "/filter regen", desc: "only show re-drafts" },
  { cmd: "/filter web", desc: "only show web searches" },
  { cmd: "/filter cite", desc: "only show citation swaps" },
  { cmd: "/filter prompt", desc: "only show prompts you sent" },
  { cmd: "/filter word", desc: "only show your Word edits" },
  { cmd: "/filter approval", desc: "only show approval decisions" },
  {
    cmd: "/filter §",
    desc: "only show events for one section — e.g. /filter §9.2",
  },
  { cmd: "/filter you", desc: "only show events YOU caused" },
  { cmd: "/filter asthra", desc: "only show events Asthra caused" },
  { cmd: "/filter clear", desc: "remove the filter" },
];

const ACT_BASE: ReadonlyArray<MenuItem> = [
  { cmd: "/stop", desc: "stop the current run and return to chat" },
  {
    cmd: "/replay",
    desc: "step through the timeline one event at a time (respects the active filter)",
  },
  {
    cmd: "/export",
    desc: "download the audit trail as JSON (or /export csv for a spreadsheet)",
  },
  { cmd: "/web", desc: "search external literature — e.g. /web ICH E3 §2.1" },
  { cmd: "/citations", desc: "show citations the agent has used" },
  { cmd: "/images", desc: "browse the library's figures and insert one into the document" },
  {
    cmd: "/abbreviations",
    desc: "build the abbreviations table from the drafted sections and insert it",
  },
  {
    cmd: "/regen",
    desc: "redraft the section your cursor is in — e.g. /regen tighten the safety narrative",
  },
  // /undo stays disabled-in-the-catalog; see file-head DECISION.
  {
    cmd: "/undo",
    desc: "coming soon — revert the last action",
    disabled: true,
  },
];

const REGEN_ITEM: MenuItem = ACT_BASE.find((i) => i.cmd === "/regen") as MenuItem;
const ABBREVIATIONS_ITEM: MenuItem = ACT_BASE.find((i) => i.cmd === "/abbreviations") as MenuItem;

/** State-gated /abbreviations row — enabled only post-run (it scans the
 *  DRAFTED sections). Same shape as {@link regenActItem}. Pure. */
function abbreviationsActItem(state: MenuRenderState): MenuItem {
  // Enabled whenever there are stable drafted sections to scan — i.e. a
  // completed run OR a paused run (both persist draft bodies the server
  // can read). NOT while actively drafting (mid-run/pausing/approval),
  // where the live doc is still changing under an inserted table.
  if (state.runState === "post-run" || state.runState === "paused") return ABBREVIATIONS_ITEM;
  return {
    ...ABBREVIATIONS_ITEM,
    disabled: true,
    disabledReason: "draft or pause a run first — the table is built from the drafted sections",
  };
}

/** State-gated /regen row — enabled only post-run (it redrafts a
 *  DRAFTED section). Shared by the full menu's Act loop and the
 *  restricted (advanced-commands off) menu so both surfaces gate
 *  identically. Pure. */
function regenActItem(state: MenuRenderState): MenuItem {
  if (state.runState === "post-run") return REGEN_ITEM;
  return {
    ...REGEN_ITEM,
    disabled: true,
    disabledReason: "finish the run first — /regen redrafts a drafted section",
  };
}

const SYSTEM_ITEMS: ReadonlyArray<MenuItem> = [
  { cmd: "/settings", desc: "open settings" },
  { cmd: "/logout", desc: "log out of this session" },
];

// ---------------------------------------------------------------------------
// Legacy export — retained for tests that enumerate the full catalog
// ---------------------------------------------------------------------------

/** The un-filtered union of every menu group. Useful for tests
 *  that want to round-trip every command through parseCommand. The
 *  rendered menu uses ``buildMenuGroups`` which applies state. */
export const MENU_GROUPS: ReadonlyArray<MenuGroup> = [
  START_GROUP,
  { label: "Pick", items: PICK_BASE },
  { label: "Navigate", items: NAVIGATE_ITEMS },
  { label: "Filter", items: FILTER_ITEMS },
  { label: "Act", items: ACT_BASE },
  { label: "System", items: SYSTEM_ITEMS },
];

// ---------------------------------------------------------------------------
// buildMenuGroups — state-aware filter. Pure function; tests pin
// each gating rule.
// ---------------------------------------------------------------------------

/**
 * Build the filtered menu for the writer's current state. Hides
 * whole groups that don't apply, disables individual items that
 * are valid grammar but invalid in the current state, and
 * expands per-workspace / /mode variants.
 *
 * Pure — identical input → identical output. No React, no side
 * effects.
 */
export function buildMenuGroups(state: MenuRenderState): MenuGroup[] {
  // Feature-flag gate: when advanced-commands is off, restrict the menu
  // to ALWAYS_ALLOWED_COMMANDS + essential System actions.
  if (!state.advancedCommandsEnabled) {
    const restrictedGroups: MenuGroup[] = [
      // /draft (idle only, same rule as full menu)
      ...(state.runState === "idle" || state.runState === "post-run" ? [START_GROUP] : []),
      // /library only from the Pick group
      {
        label: "Pick" as MenuGroupLabel,
        items: PICK_BASE.filter((item) => ALWAYS_ALLOWED_COMMANDS.has(item.cmd)),
      },
      // /regen — core drafting-loop (post-run counterpart of /draft);
      // same state gating as the full menu, so it renders disabled
      // with a reason before a run finishes rather than vanishing.
      // /abbreviations rides alongside it (also post-run gated).
      { label: "Act" as MenuGroupLabel, items: [regenActItem(state), abbreviationsActItem(state)] },
      // System (/logout, /settings) always accessible
      { label: "System" as MenuGroupLabel, items: SYSTEM_ITEMS },
    ];
    return restrictedGroups.filter((g) => g.items.length > 0);
  }

  const groups: MenuGroup[] = [];
  const runActive = state.runState !== "idle";

  // Start — /draft is idle-only. Mid-run / approval / post-run
  // replace with "start a new draft" by hiding the group until
  // the writer /stops the current run.
  if (!runActive) {
    groups.push(START_GROUP);
  }

  // Pick — always visible, but /mode label + disabled state come
  // from current state. /upload is disabled mid-run / approval
  // because the backend + agent are mid-read on the library's
  // contents — uploading / deleting would race those reads.
  const modeItem: MenuItem = {
    cmd: state.isSandbox ? "/mode live" : "/mode sandbox",
    desc: state.isSandbox
      ? "switch to LIVE mode (uses the real agent — costs API credits)"
      : "switch to SANDBOX mode (replays a recorded session locally)",
    disabled: state.modeToggling || !state.modeToggleAllowed,
    disabledReason: state.modeToggling
      ? "switching mode…"
      : !state.modeToggleAllowed
        ? "finish or /stop the current run first"
        : undefined,
  };
  // Per-item gate for /upload: disable when the writer is in the
  // middle of a run or an approval. /upload is grammar-valid in
  // those states but UX-invalid (race against Asthra's reads).
  const uploadLocked = state.runState === "mid-run" || state.runState === "approval" || state.runState === "paused";
  const pickItemsWithUploadGate = PICK_BASE.map((item) =>
    item.cmd === "/upload" && uploadLocked
      ? {
          ...item,
          disabled: true,
          disabledReason: "library is locked while a run is live",
        }
      : item,
  );
  const workspaceItems = buildWorkspaceItems(state);
  groups.push({
    label: "Pick",
    items: [...pickItemsWithUploadGate, modeItem, ...workspaceItems],
  });

  // Navigate — run-only (spine / timeline / jump / next / latest
  // all relate to a run's sections + events).
  if (runActive) {
    groups.push({ label: "Navigate", items: NAVIGATE_ITEMS });
  }

  // Filter — run-only AND events-exist. Filtering an empty log
  // would produce no visible change.
  if (runActive && state.hasEvents) {
    groups.push({ label: "Filter", items: FILTER_ITEMS });
  }

  // Act — hybrid. /stop is run-only; /replay + /export need
  // events; /web + /citations + /undo always render.
  const actItems: MenuItem[] = [];
  for (const item of ACT_BASE) {
    if (item.cmd === "/stop") {
      // /stop only meaningful while a run is cancellable.
      if (state.runState === "mid-run" || state.runState === "approval" || state.runState === "paused") {
        actItems.push(item);
      }
      continue;
    }
    if (item.cmd === "/replay" || item.cmd === "/export") {
      // Meaningful when there's history to replay / export.
      if (state.hasEvents) {
        actItems.push(item);
      } else {
        actItems.push({
          ...item,
          disabled: true,
          disabledReason: "no events yet — start a run first",
        });
      }
      continue;
    }
    if (item.cmd === "/regen") {
      // Redraft targets a DRAFTED section — only meaningful post-run.
      // Shared gating with the restricted menu (regenActItem).
      actItems.push(regenActItem(state));
      continue;
    }
    if (item.cmd === "/abbreviations") {
      // Built from the drafted sections — only meaningful post-run.
      actItems.push(abbreviationsActItem(state));
      continue;
    }
    actItems.push(item);
  }
  groups.push({ label: "Act", items: actItems });

  // System — always visible.
  groups.push({ label: "System", items: SYSTEM_ITEMS });

  return groups;
}

/** Expand the workspace list into one menu item per workspace.
 *  Empty/singleton workspaces lists produce no rows — the base
 *  ``/workspace`` entry stays as the writer's entry point. */
function buildWorkspaceItems(state: MenuRenderState): MenuItem[] {
  if (state.workspaces.length < 2) return [];
  return state.workspaces.map((ws) => ({
    cmd: `/workspace ${ws.name}`,
    desc: ws.isCurrent ? `currently active — '${ws.name}'` : `switch to '${ws.name}'`,
    disabled: ws.isCurrent,
    disabledReason: ws.isCurrent ? "already active" : undefined,
  }));
}

// ---------------------------------------------------------------------------
// Filter-as-you-type — narrows the menu to items matching the
// writer's typed query. Pure; tested in isolation.
// ---------------------------------------------------------------------------

/**
 * Filter menu groups by a writer-typed query. Ranked:
 *   rank 0 — cmd (minus leading "/") STARTS WITH the token
 *   rank 1 — cmd contains the token mid-string
 *   rank 2 — desc contains the token; cmd does NOT
 *
 * # DECISION: desc-only matches (rank 2) are dropped from the
 * #   result when ANY cmd match (rank 0 or 1) survives.
 * # WHY: writer feedback 2026-04-23 — typing "/librar" was
 * #      matching /draft (desc: "…pick doctype, library, sections")
 * #      because desc happens to mention libraries. /draft got
 * #      auto-highlighted because Start group renders first, and
 * #      Tab filled the wrong command. The fix treats cmd matches
 * #      as semantic intent ("writer is typing this command")
 * #      and desc matches as discoverability ("this command's
 * #      copy mentions this concept"). When both are on offer,
 * #      intent wins.
 * # DATE: 2026-04-23
 *
 * Empty groups are dropped from the result. Multi-token queries
 * use AND semantics — every token must match somewhere on the
 * item; the item's effective rank is the WORST (highest) rank
 * across its tokens.
 *
 * Pure. Exported for tests.
 */
export function filterMenuGroups(groups: ReadonlyArray<MenuGroup>, query: string): MenuGroup[] {
  const normalised = normaliseQuery(query);
  if (normalised.length === 0) return groups.map((g) => ({ ...g }));

  const tokens = normalised.split(/\s+/).filter((t) => t.length > 0);
  if (tokens.length === 0) return groups.map((g) => ({ ...g }));

  // Collect ranked matches across all groups.
  const ranked: Array<{
    groupLabel: MenuGroupLabel;
    item: MenuItem;
    rank: number;
  }> = [];
  for (const group of groups) {
    for (const item of group.items) {
      const r = rankItem(item, tokens);
      if (r !== null) ranked.push({ groupLabel: group.label, item, rank: r });
    }
  }
  if (ranked.length === 0) return [];

  // If any cmd-match exists (rank <= 1), drop desc-only matches.
  // Desc-only falls back when NO cmd-match exists — preserves
  // discoverability for freeform searches like "download" (which
  // only appears in /export's desc).
  const hasCmdMatch = ranked.some((r) => r.rank <= 1);
  const kept = hasCmdMatch ? ranked.filter((r) => r.rank <= 1) : ranked;

  // Reconstruct groups in original order. Items within a group
  // preserve their catalog order (we don't re-sort within) —
  // writers scanning groups see the same relative positions they
  // know, just with inferior matches filtered out.
  const itemsByGroup = new Map<MenuGroupLabel, MenuItem[]>();
  for (const { groupLabel, item } of kept) {
    const list = itemsByGroup.get(groupLabel);
    if (list) list.push(item);
    else itemsByGroup.set(groupLabel, [item]);
  }
  const result: MenuGroup[] = [];
  for (const group of groups) {
    const items = itemsByGroup.get(group.label);
    if (items && items.length > 0) {
      result.push({ label: group.label, items });
    }
  }
  return result;
}

/** Worst rank across tokens; null when any token fails to match. */
function rankItem(item: MenuItem, tokens: ReadonlyArray<string>): number | null {
  let worst = 0;
  for (const tok of tokens) {
    const r = rankToken(item, tok);
    if (r === null) return null;
    if (r > worst) worst = r;
  }
  return worst;
}

/**
 * Match rank for a single token against a single item.
 * 0 = cmd body starts with token (best — writer is typing this
 *     command's name).
 * 1 = cmd body contains the token mid-string (still intent).
 * 2 = desc contains the token but cmd does not (discoverability
 *     match — weakest).
 * null = no match.
 */
function rankToken(item: MenuItem, token: string): number | null {
  const cmdBody = item.cmd.toLowerCase().replace(/^\//, "");
  const descLower = item.desc.toLowerCase();
  if (cmdBody.startsWith(token)) return 0;
  if (cmdBody.includes(token)) return 1;
  if (descLower.includes(token)) return 2;
  return null;
}

function normaliseQuery(raw: string): string {
  if (typeof raw !== "string") return "";
  const trimmed = raw.trim();
  const stripped = trimmed.startsWith("/") ? trimmed.slice(1) : trimmed;
  return stripped.toLowerCase().trim();
}

// ---------------------------------------------------------------------------
// Auto-open predicate
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Keyboard-navigation helpers
// ---------------------------------------------------------------------------

/**
 * Flatten a list of groups into a single ordered array of items,
 * preserving the group-then-position order the menu renders.
 * Used by the rail to map a single ``highlightedIndex`` into the
 * correct item for arrow-key navigation + Enter-to-select.
 *
 * Pure; no dedupe (groups shouldn't contain the same cmd anyway).
 */
export function flattenMenuItems(groups: ReadonlyArray<MenuGroup>): MenuItem[] {
  const out: MenuItem[] = [];
  for (const group of groups) {
    for (const item of group.items) {
      out.push(item);
    }
  }
  return out;
}

/**
 * Advance a highlighted-index by delta (+1 for ArrowDown, -1 for
 * ArrowUp), wrapping at the list boundaries. Returns the original
 * index when the list is empty (caller's job to not fire nav
 * against nothing). Pure.
 */
export function nextHighlightedIndex(currentIndex: number, delta: 1 | -1, totalItems: number): number {
  if (totalItems <= 0) return currentIndex;
  const raw = currentIndex + delta;
  // JavaScript's modulo can go negative; handle explicitly.
  const wrapped = ((raw % totalItems) + totalItems) % totalItems;
  return wrapped;
}

// ---------------------------------------------------------------------------
// Auto-open predicate
// ---------------------------------------------------------------------------

/**
 * Decide whether typing in the rail input should auto-open the
 * menu. Pure helper exported for tests.
 *
 * Rules (per Spec 26 §5 Q5):
 *   - Input starts with "/" (anywhere the writer's typing a
 *     command: "/", "/s", "/filter xxx" all open)
 *   - Otherwise → don't open (writer is writing freeform chat)
 *
 * Spec 29 follow-up: widened from "only open on bare / or / after
 * whitespace" to "open whenever input starts with /" so the
 * filter-as-you-type narrowing is visible as the writer types.
 *
 * Returns false on any non-string input (defensive).
 */
export function shouldAutoOpenMenu(value: string): boolean {
  if (typeof value !== "string") return false;
  return value.trimStart().startsWith("/");
}
